import { Avatar, Card, CardActionArea, Chip, CircularProgress, Dialog, DialogActions, DialogContent, Fab, List, ListItem, ListItemSecondaryAction, ListItemText, Typography, makeStyles, useMediaQuery } from '@material-ui/core';
import { AddCircle, AddCircleOutline, CheckCircle, Close } from '@material-ui/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { fmtColor } from 'util/format';
import { localizeCurrency } from 'util/localizeFormat';
import { useTrackEvent } from 'util/trackEvent';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    height: 300*.6,
    width: 225*.7,
    flex: 'none',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      boxShadow: theme.shadows[10],
      transform: 'scale(1.05)',
    },
    cursor: 'pointer',
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    alignItems: 'flex-start',
    position: 'relative',
  },
  serviceIcon: {
    width: 'auto',
    flex: 0,
    height: 315*.6,
    alignSelf: 'flex-end',
    objectFit: "cover",
    objectPosition: "bottom",
    position: 'absolute',
    bottom: 0,
    right: -20,
    zIndex: 0
  },
  serviceInfo: {
    flex: 1,
    alignSelf: 'normal',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    padding: theme.spacing(1),
    alignItems: 'flex-start',

    "&>h3": {
      textShadow: '2px 2px 5px rgba(0,0,0,0.10)'
    },

    color: theme.palette.common.white,
    "&>h6": {
      paddingBottom: theme.spacing(1.5)
    },
    zIndex: 1
  },
  serviceAction: {
    flex: 0,
    width: 75,
  },
  svcIcon: {
    height: theme.spacing(1.5),
    width: 'auto',
  },
  chip: {
    boxShadow: theme.shadows[1],
  },
  dialogPaper: {
    background: (props) => `linear-gradient(30deg, ${fmtColor(props?.header_start_color)} 0%, ${fmtColor(props?.header_end_color)} 100%)`
  },
  topBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: 'transparent'
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: (props) => fmtColor(props.header_text_color),

  },
  listContent: {
    backgroundColor: theme.palette.background.paper
  }
}));

const ItemCheckList = ({ selected, onClick, item }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(false);
  return (
    <ListItem
      button
      onClick={async () => {
        setLoading(true)
        await onClick()
        setLoading(false)
      }}
    >
      <ListItemText
        primary={item?.label}
        secondary={localizeCurrency(item?.fee)}
      />

      {loading ? <CircularProgress size={20} /> :
        selected ? <CheckCircle color='primary' /> : <AddCircleOutline color='primary' />
      }
    </ListItem>
  )
}

const PackageCategoryCard = ({
  service,
  services,
  onSelect = async (services) => { },
  selectedServices = []
}) => {
  const styleProps = {
    header_start_color: service?.start_color,
    header_end_color: service?.end_color,
    header_text_color: 'FFF',
  }
  const classes = useStyles(styleProps)

  const [showDialog, setShowDialog] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"),
    {
      noSsr: true,
    });

  const [relatedServices, setRelatedServices] = useState([]);
  const trackEvent = useTrackEvent()

  const [loading, setLoading] = useState(false);

  useEffect(
    () => {
      const filterServices = services?.filter(s => {
        return s?.category_ids?.includes(service?.id)
      })
      setRelatedServices(filterServices)
    }, [service, services]
  );
  return (
    <>
      <Dialog
        maxWidth="sm"
        scroll='paper'
        fullWidth
        fullScreen={isMobile}
        open={showDialog}
        onClose={() => {
          trackEvent('Package Card List - Close')
          setShowDialog(false)
        }}
        classes={{
          paper: classes.dialogPaper
        }}
      >
        <DialogActions className={classes.topBar}
        >
          <div />
          <div className={classes.header}>
            <Typography variant="h4">
              {service?.label}
            </Typography>
            <Typography variant="body2">
              {service?.description}
            </Typography>
          </div>
          <Fab
            onClick={() => {
              trackEvent('Create Booking Package Card List FAB- Close')
              setShowDialog(false)
            }}
            size="small"
            color="secondary"
            aria-label="close">
            <Close />
          </Fab>
        </DialogActions>
        <DialogContent className={classes.listContent}>
          <List dense>
            {relatedServices?.map((s, idx) => {
              const selected = selectedServices.find(x => x?.id === s?.id)
              return (
                <ItemCheckList key={idx}
                  item={s}
                  selected={selected}
                  onClick={async () => {
                    if (selected) {
                      trackEvent('Create Booking Package Card List - Remove')
                      await onSelect(selectedServices.filter(x => x?.id !== s?.id))
                    } else {
                      trackEvent('Create Booking Package Card List - Add')
                      await onSelect([...selectedServices, s])
                    }
                  }} />
              )
            })}
          </List>
        </DialogContent>
      </Dialog>
      <Card
        className={classes.root}
        style={{
          background: `linear-gradient(30deg, ${fmtColor(service?.start_color)} 0%, ${fmtColor(service?.end_color)} 100%)`,
        }}>
        {/* {json} */}
        <CardActionArea onClick={() => {
          trackEvent('Create Booking Package Card - Open')
          setShowDialog(true)
        }} className={classes.container}>
          <img className={classes?.serviceIcon}
            src={`${service?.icon_url}`}
          />
          <div className={classes?.serviceInfo}>
            <Typography
              style={{
                color: fmtColor(service?.text_color)
              }}
              variant='h5'>{service?.label}</Typography>

            <div style={{ flex: 1, }} />
            {service?.booking_type?.label && (
              <Chip
                size="small"
                className={classes.chip}
                style={{
                  color: service?.booking_type?.fg_color,
                  backgroundColor: service?.booking_type?.bg_color,
                }}
                icon={
                  <Avatar variant="square" className={classes.svcIcon} src={service?.booking_type?.icon_url} />
                }
                label={service?.booking_type?.label}
              />
            )}
          </div>

        </CardActionArea>
      </Card>
    </>
  )
}

export { PackageCategoryCard }